import React, { memo, useCallback, useMemo, useState } from 'react';
import Div from '@/shared/Div';
import VideoBanner, { BannerComponentType } from '@/shared/crisp/VideoBanner';
import SlickSlider from '@/shared/crisp/SlickSlider';
import is from '@/utils/is';
import SingleImagesBanner, { SingleImagesBannerProps } from '@/shared/crisp/SingleImagesBanner';
import withContentFlags from '@/hoc/withContentFlags';
import ChevronIcon from '../Icons/Chevron';
import useScreen from '@/hooks/useScreen';

interface BannerItem extends SingleImagesBannerProps {
  id: string;
  speed: number;
  type?: BannerComponentType;
  mobileMedia: SingleImagesBannerProps['mobileImage'];
  desktopMedia: SingleImagesBannerProps['desktopImage'];
}

interface HeroBannerCarouselProps {
  list: BannerItem[];
  clickable: boolean;
}

const HeroBannerCarousel = ({ list, clickable = false }: HeroBannerCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { isDesktop } = useScreen();
  const renderBanner = useCallback((item: BannerItem) => {
    const { href, type, mobileMedia, desktopMedia } = item;

    if (href && type === BannerComponentType.Image) {
      return (
        <SingleImagesBanner
          mobileImage={mobileMedia}
          desktopImage={desktopMedia}
          href={href}
          title={''}
          subTitle={''}
          ctas={[]}
        />
      );
    }

    return <VideoBanner key={item.id} dataTestId={`banner-${item.id}`} {...item} />;
  }, []);

  const settings = useMemo(() => {
    const config: any = {
      arrows: isDesktop,
      autoplay: true,
      dots: true,
      autoplaySpeed: list[currentIndex]?.speed,
      afterChange: (index: number): React.ReactNode => {
        setCurrentIndex(index);
        return null;
      },
      infinite: is.not.empty.array(list) && list.length > 1 ? true : false,
      prevArrow: (
        <Div className="slick-prev">
          <Div className="rotate-180">
            <ChevronIcon className="text-cti-primary" size={40} />
          </Div>
        </Div>
      ),
      nextArrow: (
        <Div className="slick-next">
          <Div>
            <ChevronIcon className="text-cti-primary" size={40} />
          </Div>
        </Div>
      ),
    };

    if (clickable) {
      config['appendDots'] = (dots: React.ReactNode) => {
        return (
          <Div
            style={{
              magrin: 'auto',
              backgroundColor: '#F9F9F9',
            }}
          >
            <ul> {dots} </ul>
          </Div>
        );
      };
    }

    return config;
  }, [list, clickable, isDesktop]);

  const slickSlider = (
    <SlickSlider settings={settings} className="main-slider" data={list} render={renderBanner} />
  );

  if (clickable) {
    return <Div className="relative"> {slickSlider}</Div>;
  }

  return (
    <Div dataTestId="hero-banner" className="bg-cm-primary pb-s8 md:pb-s9">
      {slickSlider}
    </Div>
  );
};

export default withContentFlags(memo(HeroBannerCarousel));
